import { cx, cy } from '../game/geometry';
import type { NodeRole, Point } from '../game/types';

/** Anything a wire can terminate on. */
export type AttachRole = NodeRole | 'house';

export interface Vec2 {
  x: number;
  y: number;
}

type Conductors = { L: [number, number]; C: [number, number]; R: [number, number] };

/**
 * Each sprite's conductor points, as offsets from the cell centre.
 *
 * L/C/R must coincide with the amber conductor dots drawn on the sprite —
 * if a sprite moves, these move with it or the wires float.
 */
const CONDUCTORS: Record<AttachRole, Conductors> = {
  // the mast dot
  plant: { L: [12, -34], C: [12, -34], R: [12, -34] },
  // the suspension tower: arm-tip insulators + the peak
  standard: { L: [-17.5, -21.3], C: [0, -28.2], R: [17.5, -21.3] },
  // outer V-strings under the crossarms + the wishbone in the head
  junction: { L: [-18, -3], C: [0, -31], R: [18, -3] },
  // same, scaled ×1.08/×1.18 about the ground line at y=34
  crossing: { L: [-19.44, -9.66], C: [0, -42.7], R: [19.44, -9.66] },
  // the service mast
  house: { L: [0, -30], C: [0, -30], R: [0, -30] },
};

/** Beyond this horizontal offset a wire lands on an arm tip rather than the peak. */
const ARM_THRESHOLD = 35;

/** Where a wire meets a sprite, given what it's heading toward. */
export function attachPoint(n: Point, role: AttachRole, towardX: number): Vec2 {
  const t = CONDUCTORS[role];
  const dx = towardX - cx(n);
  const p = dx < -ARM_THRESHOLD ? t.L : dx > ARM_THRESHOLD ? t.R : t.C;
  return { x: cx(n) + p[0], y: cy(n) + p[1] };
}

/** A catenary-ish quadratic — longer spans sag more. */
export function wirePath(p1: Vec2, p2: Vec2): string {
  const len = Math.hypot(p2.x - p1.x, p2.y - p1.y);
  const sag = 8 + len * 0.07;
  return `M${p1.x} ${p1.y} Q${(p1.x + p2.x) / 2} ${(p1.y + p2.y) / 2 + sag} ${p2.x} ${p2.y}`;
}
